import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { ResizableImageExtension } from './ResizableImageExtension';
import Color from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Underline from '@tiptap/extension-underline';
import { useEffect } from 'react';
import { FormattingToolbar } from './FormattingToolbar';
import { MaxNestingExtension } from './MaxNestingExtension';

export interface EntryEditorProps {
  /** TipTap JSON content as a string */
  content: string;
  /** Callback fired when editor content changes. Receives TipTap JSON as string. */
  onChange: (json: string) => void;
  /** Whether the editor is editable or read-only */
  editable: boolean;
}

/**
 * EntryEditor wraps a TipTap editor instance with custom extensions for the Journal App.
 * Supports rich text formatting, checklists, images, text color, and enforces
 * a maximum list nesting depth of 3.
 *
 * Requirements: 2.1, 2.2, 2.5, 2.6, 2.9
 */
export function EntryEditor({ content, onChange, editable }: EntryEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // StarterKit provides: bold, italic, strike, headings, bulletList, orderedList, horizontalRule, etc.
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      ResizableImageExtension.configure({
        inline: true,
        allowBase64: true,
      }),
      TextStyle,
      Color,
      Underline,
      TaskList.configure({
        HTMLAttributes: {
          class: 'task-list',
        },
      }),
      TaskItem.configure({
        nested: true, // Enable checkbox support with nesting
        HTMLAttributes: {
          class: 'task-item',
        },
      }),
      MaxNestingExtension.configure({
        maxDepth: 3,
      }),
    ],
    content: parseContent(content),
    editable,
    onUpdate: ({ editor }) => {
      const json = JSON.stringify(editor.getJSON());
      onChange(json);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[200px] p-4',
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': 'Journal entry editor',
      },
    },
  });

  // Update editable state when prop changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(editable);
    }
  }, [editor, editable]);

  // Update content when prop changes (e.g. switching entries)
  useEffect(() => {
    if (editor && content) {
      const currentContent = JSON.stringify(editor.getJSON());
      if (currentContent !== content) {
        const parsed = parseContent(content);
        editor.commands.setContent(parsed, false);
      }
    }
  }, [editor, content]);

  return (
    <div className="flex flex-col border border-border rounded-sm bg-bg-card shadow-card overflow-hidden">
      {editable && <FormattingToolbar editor={editor} />}
      <EditorContent
        editor={editor}
        className="entry-editor-content"
      />
    </div>
  );
}

/**
 * Parse content string into TipTap-compatible JSON or fallback to empty doc.
 */
function parseContent(content: string): Record<string, any> {
  if (!content || content.trim() === '') {
    return {
      type: 'doc',
      content: [{ type: 'paragraph' }],
    };
  }
  try {
    return JSON.parse(content);
  } catch {
    return {
      type: 'doc',
      content: [{ type: 'paragraph' }],
    };
  }
}

export default EntryEditor;
